import { Component, DoCheck, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { Observable } from 'rxjs';
import { FrontProfilesService } from '@services/front-profiles/front-profiles.service';
import { AdditionalProfileDataService } from '@services/additional-profile-data/additional-profile-data.service';
import phone from 'phone';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DialogService } from '@services/dialog/dialog.service';
import { AccountService } from '@services/accounts/account.service';
import { GroupHasRoleInOrganisationService } from '@services/groups/group-has-role-in-organisation.service';
import { GroupService } from '@services/groups/group.service';
import { GroupHasFrontProfileService } from '@services/groups/group-has-frontprofile.service';
import { AddItemsFromList } from '../../dialogs/add-items-from-list/add-items-from-list.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@services/translate/translate.service';
import { PackageService } from '@services/packages/package.service';
import { MessengerService } from '@services/messenger/messenger.service';

@Component({
  selector: 'frontendprofile-edit-form',
  templateUrl: './frontendprofile-edit-form.component.html',
  styleUrls: ['./frontendprofile-edit-form.component.scss'],
  providers: [FrontProfilesService, AdditionalProfileDataService, AccountService, GroupHasRoleInOrganisationService,
    GroupService, GroupHasFrontProfileService],
  standalone: false
})
export class FrontendprofileEditFormComponent implements OnInit, DoCheck {
  @Input() organisationId;
  @Input() itemId;

  @Output() changed: EventEmitter<boolean> = new EventEmitter();
  hasChanged = false;

  history: any;
  systemMessages: any;
  title;
  profile: Models.FrontProfileModel;
  additionalFieldConfig: any;
  public additionalFieldKeys: any;
  public additionalProfileData: Models.AdditionalProfileData;
  public errorMessage: { email: boolean, mobile: boolean, channel: boolean, message: string, name: boolean, key: string };
  private pak: Models.Package;
  private ghrio: Models.GroupHasRoleInOrganisation[];
  private ghfp: Models.GroupHasFrontProfile[];

  activeAddressbooks: any[];
  addressbooks: any[];
  activeGroups: any[];
  groups: any[];
  groupColumns = [{
    prop: 'name', translationString: 'userAdmin.frontend-group.name',
    valueTransformer: (val) => (val || '').startsWith('pref_') ? val.split('_').splice(2, val.split('_').length - 1).join('_') : val
  }, { prop: 'GroupID' }];

  headerButtonsGroup;
  headerButtonsAddressbook;

  isAddressbookAdmin = false;
  isGroupAdmin = false;

  constructor(
    private translate: TranslateService, private profileService: FrontProfilesService, private packageService: PackageService, private route: ActivatedRoute,
    private additionalDataService: AdditionalProfileDataService, private snackbar: SnackbarService, private dialogService: DialogService, private accountService: AccountService,
    private router: Router, private ghrioService: GroupHasRoleInOrganisationService, private groupService: GroupService, private ghfpService: GroupHasFrontProfileService,
    private dialog: MatDialog, private ms: MessengerService
  ) {
    this.ms.$activeRoles.subscribe(roles => {
      this.isAddressbookAdmin = !!roles?.find((el: any) => el?.roleName === 'chatModuleAdmin');
      this.isGroupAdmin = !!roles?.find((el: any) => ['ticketingModuleAdmin', 'orderModuleAdmin', 'securedContentAdmin'].includes(el?.roleName));
    });
  }

  @HostListener('window:beforeunload')
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(): Observable<boolean> | boolean | Promise<boolean> {
    return this.handleBeforeUnload();
  }

  async ngOnInit() {
    this.title = this.translate.get('userAdmin.profile.new');
    this.systemMessages = this.translate.get('system');
    this.organisationId = this.route.snapshot.params['id'] || this.ms.getOrganisation().OrganisationID;
    const pakId = this.route.snapshot.params['packageId'];
    if (!pakId) {
      const paks = await this.packageService.find(0, 1000, { OwnerOrganisationID: this.organisationId, 'ibexApp.enabled': true });
      this.pak = paks?.data?.pop?.();
    } else {
      this.pak = await this.packageService.get(this.route.snapshot.params['packageId']);
    }
    this.additionalFieldConfig = this.pak.additionalFrontProfileFields;
    await this.initProfile();
    this.initHistory();
  }

  async initProfile() {
    if (this.itemId) {
      this.profile = await this.profileService.get(this.itemId);
      if ((this.profile.channel === 'email' && !this.profile.email) || (this.profile.channel === 'mobile' && !this.profile.mobile)) {
        this.profile.channel = this.profile.channel === 'email' ? 'mobile' : 'email';
      }
      if (this.additionalFieldConfig && this.profile.additionalDataId) {
        try {
          this.additionalProfileData = await this.additionalDataService.get(this.profile.additionalDataId);
          this.additionalFieldKeys = Object.keys(this.additionalProfileData.data);
          for (const key of this.additionalFieldKeys) {
            this.additionalProfileData.data[key] = this.additionalProfileData.data[key]?.toString() || '';
          }
        } catch (err) {
          this.initAdditionalData();
        }
      } else if (this.additionalFieldConfig) {
        this.initAdditionalData();
      }
    } else {
      this.profile = new Models.FrontProfileModel();
      this.profile.populateFromJSON({ packageId: this.route.snapshot.params['packageId'], currentLanguage: 'de', isAnonymous: 0 });

      if (this.additionalFieldConfig && Object.keys(this.additionalFieldConfig)?.length) {
        this.initAdditionalData();
      }
    }
    this.ghrio = (await this.ghrioService.find(0, 1000, { OrganisationID: this.organisationId, GroupID: { $ne: 8 } })).data;
    this.addressbooks = (await this.groupService.find(0, this.ghrio.length, {
      GroupID: { $in: this.ghrio.filter(el => [48, 52].includes(parseInt(el.RoleID, 10))).map(el => el.GroupID) }
    })).data;
    this.groups = (await this.groupService.find(0, this.ghrio.length, {
      GroupID: { $in: this.ghrio.filter(el => [45, 49, 53, 55].includes(parseInt(el.RoleID, 10))).map(el => el.GroupID) }
    })).data;
    this.ghfp = (await this.ghfpService.find(0, this.ghrio.length, { ProfileID: this.itemId })).data;
    this.activeAddressbooks = this.addressbooks.filter(el => !!this.ghfp.find(ghfp => ghfp.GroupID === el.GroupID));
    this.activeGroups = this.groups.filter(el => !!this.ghfp.find(ghfp => ghfp.GroupID === el.GroupID));
    this.updateHeaderButtons();
  }

  initAdditionalData() {
    this.additionalProfileData = new Models.AdditionalProfileData();
    this.additionalProfileData.populateFromJSON({ data: {} });
    for (const key of Object.keys(this.additionalFieldConfig)) {
      this.additionalProfileData.data[key] = '';
    }
    this.additionalFieldKeys = Object.keys(this.additionalProfileData.data);
  }

  async ngDoCheck() {
    if (this.hasChanged !== !this.handleBeforeUnload()) {
      this.hasChanged = !this.handleBeforeUnload();
      this.changed.emit(this.hasChanged);
    }
  }

  initHistory(): void {
    this.history = {};
    ['profile', 'additionalProfileData', 'activeAddressbooks', 'activeGroups'].forEach(field => { this.history[field] = JSON.stringify(this[field]) });
  }

  handleBeforeUnload() {
    return !Object.keys(this?.history || {}).some(key => this.history?.[key] !== JSON.stringify(this?.[key]));
  }

  async save() {
    this.errorMessage = await this.testUser();
    if (!this.errorMessage.channel || !this.errorMessage.name || !!this.errorMessage.key) {
      return;
    }
    if (this.profile.channel === 'email') {
      if (!this.errorMessage.mobile) {
        delete this.profile.mobile;
      }
    } else {
      if (!this.errorMessage.email) {
        delete this.profile.email;
      }
    }
    if (!this.itemId) {
      const existingProfile = await this.profileService.find(0, 1, { packageId: this.profile.packageId, firstName: this.profile.firstName, lastName: this.profile.lastName });
      if (existingProfile?.data?.length) {
        const res = await this.dialogService.confirm('Es existiert bereits ein Benutzer mit den eingegebenen Vor- und Nachnamen, möchten Sie diesen Benutzer wirklich anlegen?');
        if (!res) {
          return;
        }
      }
      if (this.additionalProfileData) {
        const res = await this.additionalDataService.create(this.additionalProfileData);
        this.profile.additionalDataId = res._id;
      }
      const newAccount = await this.accountService.create(new Models.Account());
      this.profile.AccountID = newAccount.AccountID;
      const data = this.clone(this.profile) as any;
      try {
        const newProfile: any = await this.profileService.create(data);
        await this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'], 'frontend-profiles']).then(() => {
          this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'], 'frontend-profiles', newProfile.FrontProfileID]);
        });
        this.snackbar.success('Successful created Profile');
      } catch (err) {
        this.accountService.remove(newAccount.AccountID);
        if (err?.error?.errors?.length && !!err.error.errors.find(el => el?.message.includes('unique_index'))) {
          this.errorMessage.message = 'alreadyExists';
        } else if (err.error && err.error.message) {
          try {
            err = JSON.parse(err.error.message);
            if (err.reason && err.reason === 'tooMany') {
              this.errorMessage.message = 'tooMany';
            }
          } catch (error) {
            console.warn('could not parse error message');
            this.errorMessage.message = 'alreadyExists';
          }
        } else {
          this.errorMessage.message = 'alreadyExists';
        }
        return;
      }
    } else {
      if (this.additionalProfileData) {
        if (this.additionalProfileData._id) {
          await this.additionalDataService.update(this.additionalProfileData._id, this.additionalProfileData);
        } else {
          const res = await this.additionalDataService.create(this.additionalProfileData);
          this.profile.additionalDataId = res._id;
        }
      }
      const data = this.clone(this.profile) as any;
      data['backend'] = true;
      try {
        await this.profileService.update(this.profile.FrontProfileID, data);
      } catch (err) {
        if (err?.error?.message === 'channel already existing' || err?.error?.message === 'Validation error') {
          this.errorMessage.message = 'alreadyExists';
          return;
        }
      }
      const groups = this.activeGroups.concat(this.activeAddressbooks);
      const added = this.arrayDifference(groups.map(el => el.GroupID), this.ghfp.map(el => el.GroupID));
      const removed = this.arrayDifference(this.ghfp.map(el => el.GroupID), groups.map(el => el.GroupID));
      const promises = [];
      added.forEach(groupId => {promises.push(this.ghfpService.create({ ProfileID: this.profile.FrontProfileID, GroupID: groupId }))});
      removed.map(groupId => this.ghfp.filter(g => g.GroupID === groupId)).flat().map(el => el.ID).forEach(groupId => {promises.push(this.ghfpService.remove(groupId));});
      await Promise.all(promises);
      this.initHistory();
      this.snackbar.success('Successful updated Profile');
    }
  }

  arrayDifference(arr1, arr2) {
    const set2 = new Set(arr2);
    return arr1.filter(x => !set2.has(x));
  }

  async testUser(): Promise<any> {
    const email = this.testEmail(this.profile.email);
    const mobile = this.testMobile(this.profile.mobile);
    if (!!mobile) {
      this.profile.mobile = mobile;
    }
    let channel = !!this.profile.channel;
    let message = 'channel';
    if (channel && this.profile.channel === 'email') {
      channel = email;
      message = channel ? null : 'channelSelect';
    } else if (channel && this.profile.channel === 'mobile') {
      channel = !!mobile;
      message = channel ? null : 'channelSelect';
    }
    if (!mobile && !email) {
      message = 'both';
    }
    let name = true;
    if (channel) {
      const { firstName, lastName } = this.profile;
      if (!firstName || firstName.length < 3 || !lastName || lastName.length < 3) {
        name = false;
        message = (!firstName || firstName.length < 3) ? 'firstname' : 'lastname';
      }
    }
    const res = { email, mobile: !!mobile, channel, message, name, key: null };
    for (const key of Object.keys(this.additionalFieldConfig || {})) {
      if (this.additionalFieldConfig[key]?.unique && !!this.additionalProfileData.data[key]) {
        const profiles = await this.profileService.find(0, 1000, { packageId: this.pak._id, isAnonymous: 0 });
        if (profiles?.data?.length) {
          const ids = profiles.data.filter(el => el.FrontProfileID !== this.profile?.FrontProfileID).map(el => el.additionalDataId);
          const promises = [];
          for (let i = 0; i < ids.length; i += 100) {
            promises.push(this.additionalDataService.find(0, 100, { _id: { $in: ids.slice(i, i + 100) } }));
          }
          const additional = (await Promise.all(promises)).reduce((acc, result) => acc.concat(result.data), []);
          if (additional?.length && additional.find(el => el.data[key].toString() === this.additionalProfileData.data[key].toString())) {
            res.key = key;
            res.message = 'alreadyExists';
          }
        }
      }
    }
    return res;
  }

  testEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    email = email.toString().trim();
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email.toLowerCase());
  }

  public testMobile(mobile: string): string {
    if (!mobile) {
      return null;
    }
    if (!mobile.startsWith('+')) {
      if (mobile.startsWith('0')) {
        mobile = mobile.substr(1);
      }
      mobile = '+41' + mobile;
    }
    return phone(mobile, null, false)[0];
  }

  private clone(element) {
    const newElement = {};
    for (const key of Object.keys(element)) {
      newElement[key] = element[key];
    }
    return newElement;
  }

  async addGroup(type = 'groups') {
    const active = type === 'addressbooks' ? this.activeAddressbooks : this.activeGroups;
    const all = type === 'addressbooks' ? this.addressbooks : this.groups;
    const groupIds = all.filter(el => !active.includes(el)).map(el => el.GroupID);
    let items = (await this.groupService.find(0, 1000, { GroupID: { $in: groupIds } })).data;
    items = items.map(el => {
      if ((el?.name || '').startsWith('pref_')) {
        el.name = el.name.split('_').splice(2, el.name.split('_').length - 1).join('_');
      }
      return el;
    });
    const dialogRef = this.dialog.open(AddItemsFromList, { width: '1200px', data: { availableItems: items, columns: [{ prop: 'name' }], idField: 'GroupID' } });
    const result = await dialogRef.afterClosed().toPromise();
    type === 'addressbooks' ? this.activeAddressbooks = null : this.activeGroups = null;
    result?.additionalItems?.forEach(el => {
      active.push({ GroupID: el, ProfileID: this.itemId, name: items.find(sub => sub.GroupID === el).name } as any);
    });
    setTimeout(() => {
      type === 'addressbooks' ? this.activeAddressbooks = active : this.activeGroups = active;
      this.updateHeaderButtons();
    }, 0);
  }

  async removeGroup(event: any, type = 'groups') {
    const groups = JSON.parse(JSON.stringify(type === 'addressbooks' ? this.activeAddressbooks : this.activeGroups));
    groups.splice(event.index, 1);
    type === 'addressbooks' ? this.activeAddressbooks = null : this.activeGroups = null;
    setTimeout(() => {
      type === 'addressbooks' ? this.activeAddressbooks = groups : this.activeGroups = groups;
      this.updateHeaderButtons();
    }, 0);
  }

  updateHeaderButtons() {
    this.headerButtonsGroup = [{ icon: 'add', callback: () => {return this.addGroup()}, disabled: this.activeGroups.length === this.groups.length }];
    this.headerButtonsAddressbook = [{ icon: 'add', callback: () => {return this.addGroup('addressbooks')}, disabled: this.activeAddressbooks.length === this.addressbooks.length }];
  }

  async abort() {
    if (!!this.handleBeforeUnload()) {
      this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
    } else {
      const confirmation = await this.dialogService.confirm(this.systemMessages['general']['pendingChanges']);
      if (confirmation) {
        this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
      }
    }
  }
}
